import { Lock } from 'lucide-react';

/**
 * Wraps a section that requires AI data consent.
 * When consent is absent, renders a classy locked-state card instead of children.
 *
 * Props:
 *   feature      — entry from AI_FEATURES registry ({ name, tagline, description })
 *   consent      — null | true | false
 *   onEnable     — optional callback; triggers the consent flow from parent
 *   variant      — 'full' (fills a whole tab/page) | 'section' (inline card, default)
 *   children     — the real content, rendered when consent === true
 */
export function AiGate({ feature, consent, onEnable, variant = 'section', children }) {
  if (consent === true) return children;

  const isFull = variant === 'full';

  return (
    <div
      className={
        isFull
          ? 'flex items-center justify-center min-h-[60vh] px-6'
          : 'w-full'
      }
    >
      <div
        className={`
          relative overflow-hidden
          bg-white border border-stone-100 rounded-[2rem] shadow-sm
          ${isFull ? 'w-full max-w-sm p-10' : 'p-8'}
          flex flex-col items-center text-center gap-5
        `}
      >
        {/* Subtle texture ring */}
        <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-stone-50 border border-stone-100 opacity-60" />
        <div className="absolute -bottom-8 -left-8 w-28 h-28 rounded-full bg-stone-50 border border-stone-100 opacity-40" />

        {/* Icon */}
        <div className="relative z-10 flex items-center justify-center w-14 h-14 rounded-2xl bg-stone-50 border border-stone-100 shadow-sm">
          <Lock size={20} className="text-stone-400" strokeWidth={1.5} />
        </div>

        {/* Copy */}
        <div className="relative z-10 space-y-1.5">
          <p className="text-base font-semibold text-stone-700 tracking-tight">
            {feature.name}
          </p>
          <p className="text-sm text-stone-400 leading-relaxed">
            {feature.tagline}
          </p>
        </div>

        <p className="relative z-10 text-xs text-stone-400 leading-relaxed max-w-[22ch]">
          {feature.description}
        </p>

        {/* CTA */}
        {onEnable ? (
          <button
            onClick={onEnable}
            className="relative z-10 mt-1 px-5 py-2.5 bg-stone-800 text-white text-sm font-semibold rounded-xl hover:bg-black transition-all shadow-sm"
          >
            Enable in Settings
          </button>
        ) : (
          <p className="relative z-10 text-[11px] text-stone-400 font-medium tracking-wide">
            Profile → Safety → AI Features
          </p>
        )}
      </div>
    </div>
  );
}
